/** Display dates: MMM DD YYYY (clinical) / MM-DD-YYYY when dense. */
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function formatDisplay(dt?: string | null, withTime = true): string {
  if (!dt) return "—";
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dt);
  if (dateOnly) {
    return `${MONTHS[Number(dateOnly[2]) - 1]} ${Number(dateOnly[3])} ${dateOnly[1]}`;
  }
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return String(dt);
  const date = `${MONTHS[d.getMonth()]} ${d.getDate()} ${d.getFullYear()}`;
  if (!withTime) return date;
  let h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, "0");
  const ap = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${date} ${h}:${m} ${ap}`;
}

export function toDatetimeLocal(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Clock time only — anesthesia board style (h:mm a). */
export function formatClock(dt?: string | null): string {
  if (!dt) return "—";
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return "—";
  let h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, "0");
  const ap = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${m} ${ap}`;
}

const ET = "America/New_York";

export const HALF_HOURS: string[] = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2);
  const m = i % 2 === 0 ? "00" : "30";
  return `${String(h).padStart(2, "0")}:${m}`;
});

export function formatHalfHour(hm: string): string {
  const [hStr, m] = hm.split(":");
  let h = Number(hStr);
  if (!Number.isFinite(h)) return hm;
  const ap = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${m} ${ap}`;
}

function etParts(d: Date) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: ET,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value || "";
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour: String(Number(get("hour")) % 24).padStart(2, "0"),
    minute: get("minute").padStart(2, "0"),
  };
}

function roundHalfHour(hour: number, minute: number): string {
  let h = hour;
  let m = minute < 15 ? 0 : minute < 45 ? 30 : 0;
  if (minute >= 45) h = (h + 1) % 24;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export function splitEt(iso?: string | null): { date: string; time: string } {
  if (!iso) return { date: "", time: "" };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: "", time: "" };
  const p = etParts(d);
  return {
    date: `${p.year}-${p.month}-${p.day}`,
    time: roundHalfHour(Number(p.hour), Number(p.minute)),
  };
}

export function combineEt(date: string, time: string): string | null {
  const day = date.trim();
  const hm = time.trim();
  if (!day || !hm) return null;
  const [y, mo, d] = day.split("-").map(Number);
  const [hh, mm] = hm.split(":").map(Number);
  if (![y, mo, d, hh, mm].every((n) => Number.isFinite(n))) return null;
  let utc = Date.UTC(y, mo - 1, d, hh, mm, 0);
  for (let i = 0; i < 4; i += 1) {
    const p = etParts(new Date(utc));
    const got = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), Number(p.hour), Number(p.minute), 0);
    const want = Date.UTC(y, mo - 1, d, hh, mm, 0);
    const delta = want - got;
    if (delta === 0) break;
    utc += delta;
  }
  return new Date(utc).toISOString();
}

/** Hour tick label — compact (7a, 12p, 3p). */
export function formatHourTick(dt: Date): string {
  let h = dt.getHours();
  const ap = h >= 12 ? "p" : "a";
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}${ap}`;
}

/** Wasabi backup folder stamp YYYYMMDD-HHMMSS (UTC) → MMM DD YYYY h:mm a */
export function formatBackupTimestamp(ts?: string | null): string {
  if (!ts) return "—";
  const m = /^(\d{4})(\d{2})(\d{2})-(\d{2})(\d{2})(\d{2})$/.exec(ts);
  if (!m) return ts;
  const iso = `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}Z`;
  return formatDisplay(iso);
}

/** Clinic ops calendar day (America/New_York) — not UTC (evening ET is next UTC day). */
export function todayISODate(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}
